import { useState } from 'react';
import { Send, Loader2, Mail, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useSendSMS, useSMSSettings } from '@/hooks/useSMS';
import { useSendEmail } from '@/hooks/useEmail';
import { cn } from '@/lib/utils';

type MessageMode = 'sms' | 'email';

interface MessageComposerProps {
  to: string; // Phone number for SMS
  toEmail?: string; // Email address for email
  leadId?: string;
  customerId?: string;
  trackingNumber?: string;
  onSent?: () => void;
  className?: string;
  defaultMode?: MessageMode;
}

export function MessageComposer({
  to,
  toEmail,
  leadId,
  customerId,
  trackingNumber,
  onSent,
  className,
  defaultMode = 'sms',
}: MessageComposerProps) {
  const [mode, setMode] = useState<MessageMode>(defaultMode);
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('');
  
  const { data: smsSettings } = useSMSSettings();
  const sendSMS = useSendSMS();
  const sendEmail = useSendEmail();

  const isSmsEnabled = smsSettings?.enabled;
  const isEmailEnabled = !!toEmail; // Email is enabled if we have an email address
  const isPending = sendSMS.isPending || sendEmail.isPending;

  const handleSend = async () => {
    if (!message.trim()) return;

    if (mode === 'sms') {
      if (!to) return;
      await sendSMS.mutateAsync({
        to,
        content: message.trim(),
        lead_id: leadId,
        customer_id: customerId,
        tracking_number: trackingNumber,
      });
    } else {
      if (!toEmail || !subject.trim()) return;
      await sendEmail.mutateAsync({
        to: toEmail,
        subject: subject.trim(),
        content: message.trim(),
        lead_id: leadId,
        customer_id: customerId,
      });
      setSubject('');
    }

    setMessage('');
    onSent?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // If neither SMS nor email is available, don't render
  if (!isSmsEnabled && !isEmailEnabled) {
    return null;
  }

  const canSend = mode === 'sms' 
    ? message.trim() && to && isSmsEnabled
    : message.trim() && toEmail && subject.trim();

  return (
    <div className={cn('space-y-3', className)}>
      {/* Mode toggle - only show if both modes are available */}
      {isSmsEnabled && isEmailEnabled && (
        <ToggleGroup 
          type="single" 
          value={mode} 
          onValueChange={(value) => value && setMode(value as MessageMode)}
          className="justify-start"
        >
          <ToggleGroupItem
            value="sms"
            aria-label="SMS mode"
            className={cn(
              "h-7 gap-1.5 rounded-full border border-border px-3 text-xs font-semibold",
              mode === 'sms' && "border-transparent bg-primary/10 text-primary"
            )}
          >
            <MessageSquare className="h-3.5 w-3.5" />
            SMS
          </ToggleGroupItem>
          <ToggleGroupItem
            value="email"
            aria-label="Email mode"
            className={cn(
              "h-7 gap-1.5 rounded-full border border-border px-3 text-xs font-semibold",
              mode === 'email' && "border-transparent bg-primary/10 text-primary"
            )}
          >
            <Mail className="h-3.5 w-3.5" />
            Email
          </ToggleGroupItem>
        </ToggleGroup>
      )}

      {/* Subject line for email */}
      {mode === 'email' && (
        <Input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Subject..."
          disabled={isPending}
          className="text-sm"
        />
      )}

      {/* Message input */}
      <div className="flex gap-2">
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            mode === 'sms' 
              ? `Send SMS to ${to}...` 
              : `Send email to ${toEmail}...`
          }
          disabled={isPending || (mode === 'sms' && !to) || (mode === 'email' && !toEmail)}
          className="min-h-[60px] resize-none text-[13px]"
          rows={2}
        />
        <Button
          onClick={handleSend}
          disabled={!canSend || isPending}
          size="icon"
          className="h-auto"
        >
          {isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Character count for SMS only */}
      {mode === 'sms' && (
        <p className="text-[11px] text-muted-foreground">
          {message.length}/160 characters • Press Enter to send
        </p>
      )}
      {mode === 'email' && (
        <p className="text-[11px] text-muted-foreground">
          Press Enter to send • Shift+Enter for new line • Your signature and company details are added underneath
        </p>
      )}
    </div>
  );
}
